import {
  SurvivorGameStatus,
  SurvivorLiveResult,
  SurvivorPickResult,
} from '@/features/sobrevivente/types'
import type { SurvivorGameListItem } from '@/features/sobrevivente/types'

// Sobrevivente is football-only today, but the tab bar is shared with the frozen fantasy
// home, which also renders CS2 competitions.
const SURVIVOR_SPORT = 'FOOTBALL' as const

export type CompetitionTab = {
  id:    string
  name:  string
  sport: 'FOOTBALL' | 'CS2'
}

/** One tab per competition the user has a survivor game in, in first-seen order. */
export function survivorCompetitionTabs(games: SurvivorGameListItem[]): CompetitionTab[] {
  const seen = new Set<string>()
  const tabs: CompetitionTab[] = []
  for (const game of games) {
    if (seen.has(game.competitionId)) continue
    seen.add(game.competitionId)
    tabs.push({ id: game.competitionId, name: game.competitionName, sport: SURVIVOR_SPORT })
  }
  return tabs
}

/**
 * The game that leads a competition tab: still running before finished, still alive before
 * eliminated, then whichever round kicks off soonest. A player with several games in one
 * competition sees this one on home and the rest on /sobrevivente, rather than a stack of
 * near-identical cards.
 */
export function primaryGame(games: SurvivorGameListItem[]): SurvivorGameListItem | undefined {
  const ranked = [...games].sort((a, b) => {
    const finished = rank(a.status === SurvivorGameStatus.Finished)
                   - rank(b.status === SurvivorGameStatus.Finished)
    if (finished !== 0) return finished

    const eliminated = rank(!a.isAlive) - rank(!b.isAlive)
    if (eliminated !== 0) return eliminated

    return kickoffMs(a) - kickoffMs(b)
  })
  return ranked[0]
}

function rank(flag: boolean): number {
  return flag ? 1 : 0
}

function kickoffMs(game: SurvivorGameListItem): number {
  return game.currentRound ? new Date(game.currentRound.firstMatchAt).getTime() : Infinity
}

// ── Pick presentation ─────────────────────────────────────────────────────────

export type PickTone = 'won' | 'lost' | 'live' | 'pending'

/**
 * How the caller's pick should read right now. The authoritative result wins when it has
 * resolved; until then the provisional live result stands in. A draw resolves to LOST on the
 * server, so nothing here needs to soften it.
 */
export function pickTone(
  result?: SurvivorPickResult,
  liveResult?: SurvivorLiveResult,
): PickTone {
  if (result === SurvivorPickResult.Won)     return 'won'
  if (result === SurvivorPickResult.Lost)    return 'lost'
  if (result === SurvivorPickResult.Forfeit) return 'lost'

  if (liveResult === SurvivorLiveResult.Won)  return 'won'
  if (liveResult === SurvivorLiveResult.Lost) return 'lost'
  if (liveResult === SurvivorLiveResult.Live) return 'live'

  return 'pending'
}

/**
 * Time until the round's first kickoff. Deliberately not called a deadline: entry closes
 * when an admin runs Lock, not on the clock, so promising a countdown to a cutoff would be
 * a lie. Returns null once the first match has started.
 */
export function formatFirstMatchIn(isoUtc: string, now: number = Date.now()): string | null {
  const msLeft = new Date(isoUtc).getTime() - now
  if (!Number.isFinite(msLeft) || msLeft <= 0) return null

  const totalMinutes = Math.floor(msLeft / 60_000)
  const days         = Math.floor(totalMinutes / (60 * 24))
  if (days >= 1) return `1ª partida em ${days}d`

  const hours   = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours >= 1) return `1ª partida em ${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`

  return `1ª partida em ${minutes}min`
}
